import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';

/**
 * Modal with two tabs:
 *   1. "My Hall" — one-click create @{username} hall for the signed-in user.
 *   2. "Request Global" — submit a global slug request to the Owner.
 *
 * Owner users see a third behavior: their request is auto-approved server-side.
 */
const CreateHallModal = ({ isOpen, onClose, appUser, onCreated }) => {
  const [tab, setTab] = useState('mine');
  const [ownHall, setOwnHall] = useState(null);       // existing @username hall
  const [ownLoading, setOwnLoading] = useState(false);
  const [pending, setPending] = useState(null);       // existing pending request
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !appUser?.username) return undefined;
    let cancelled = false;
    setOwnLoading(true);
    fetch(`/api/halls/@${encodeURIComponent(appUser.username)}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!cancelled && data?.hall) setOwnHall(data.hall);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setOwnLoading(false); });

    fetch('/api/halls/requests/mine', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (cancelled) return;
        const reqs = Array.isArray(data?.requests) ? data.requests : [];
        setPending(reqs.find(r => r.status === 'pending') || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, appUser?.username]);

  if (!isOpen) return null;

  const createOwnHall = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/halls/user', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok && res.status !== 409) {
        throw new Error(data?.error || 'Failed to create hall');
      }
      // On 409 ("already exists") the server returns the existing hall
      const hall = data.hall || null;
      if (hall) onCreated?.(hall);
      else onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitRequest = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/halls/requests', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: slug.trim(),
          displayName: (displayName || slug).trim()
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || data?.reason || 'Request failed');
      }
      if (data.hall) {
        // Owner auto-approved
        onCreated?.(data.hall);
      } else {
        setPending(data.request);
        alert('Your request has been submitted and is pending review.');
        onClose?.();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[200] p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-5 relative text-white">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white"
          aria-label="Close"
        >
          <FaTimes size={18} />
        </button>

        <h2 className="text-xl font-bold mb-4">Create a Hall</h2>

        <div className="flex gap-2 border-b border-gray-700 mb-4 text-sm">
          <button
            type="button"
            onClick={() => setTab('mine')}
            className={`px-3 py-2 ${tab === 'mine' ? 'border-b-2 border-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            My Hall
          </button>
          <button
            type="button"
            onClick={() => setTab('global')}
            className={`px-3 py-2 ${tab === 'global' ? 'border-b-2 border-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Request Global
          </button>
        </div>

        {error && (
          <div className="mb-3 px-3 py-2 bg-red-900 bg-opacity-60 rounded text-sm">
            {error}
          </div>
        )}

        {tab === 'mine' && (
          <div>
            {!appUser && (
              <p className="text-gray-300 text-sm">You must be signed in to create a Hall.</p>
            )}
            {appUser && ownLoading && (
              <p className="text-gray-400 text-sm">Checking for an existing @{appUser.username} hall…</p>
            )}
            {appUser && !ownLoading && ownHall && (
              <div>
                <p className="text-gray-300 text-sm mb-3">
                  Your hall <span className="font-semibold">@{appUser.username}</span> already exists.
                </p>
                <button
                  type="button"
                  onClick={() => onCreated?.(ownHall)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Go to @{appUser.username}
                </button>
              </div>
            )}
            {appUser && !ownLoading && !ownHall && (
              <div>
                <p className="text-gray-300 text-sm mb-3">
                  Create your personal hall at{' '}
                  <span className="font-mono">/@{appUser.username}</span>. You will be
                  the creator and can subscribe/promote/ban anyone who visits.
                </p>
                <button
                  type="button"
                  onClick={createOwnHall}
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-2 px-4 rounded"
                >
                  {submitting ? 'Creating…' : `Create @${appUser.username}`}
                </button>
              </div>
            )}
          </div>
        )}

        {tab === 'global' && (
          <form onSubmit={submitRequest}>
            <p className="text-gray-300 text-sm mb-3">
              Global Halls live at the URL root (e.g. <span className="font-mono">/CS2</span>). They are
              limited and must be approved by the site Owner. You can have one
              pending request at a time.
            </p>
            {pending && (
              <div className="mb-3 px-3 py-2 bg-yellow-900 bg-opacity-50 rounded text-sm">
                You already have a pending request for{' '}
                <span className="font-mono">/{pending.slug}</span>. You can submit another one
                after it's reviewed.
              </div>
            )}
            <label className="block text-xs uppercase text-gray-400 mt-2 mb-1">Slug</label>
            <input
              value={slug}
              onChange={e => setSlug(e.target.value)}
              placeholder="e.g. CS2"
              className="w-full px-3 py-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              maxLength={32}
              disabled={submitting || !!pending}
              autoFocus
            />
            <label className="block text-xs uppercase text-gray-400 mt-3 mb-1">Display name</label>
            <input
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Optional; defaults to slug"
              className="w-full px-3 py-2 rounded bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              maxLength={32}
              disabled={submitting || !!pending}
            />
            <button
              type="submit"
              disabled={submitting || !!pending || !appUser}
              className="mt-4 w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-2 px-4 rounded"
            >
              {submitting ? 'Submitting…' : (appUser?.status === 'Owner' ? 'Create (auto-approve)' : 'Submit request')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateHallModal;
